import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { getCurrentEnv } from '@tn/foundation/utils/getCurrentEnv';

/**
 * Silently re-mint the AuthSuite applicationAccessToken from the device
 * refresh token — the TN port of the recovery legacy ran via
 * checkAccessStatus() when a bind failed with TOKEN_EXPIRED.
 *
 * The SDK reads `auth-suite-deviceRefreshToken`, `auth-suite-clientId` and
 * `auth-suite-countryCode` from localStorage — all persisted by the legacy
 * /partner page under its own clientId (paramount-plus-web) — and persists
 * the fresh token pair back to the same keys. setAuthSuiteRequestData must
 * NOT be called here: it would overwrite those values with TN ones.
 *
 * Resolves null when the refresh is impossible or rejected (the SDK then
 * wipes the tokens and registers an anonymous device): the MVPD session is
 * dead and the caller should surface the normal bind error.
 */
export const refreshAuthSuiteToken = async (): Promise<string | null> => {
    try {
        // Dynamic import: only the bind-failure path pays the SDK bundle cost.
        const { initAuthSuite, refreshTokenService, UPEnv } =
            await import('@cbsi-cbscom/up-web-sdk');
        // Same env mapping as legacy AuthSuiteSdkProvider and the TN second screen.
        initAuthSuite({ env: getCurrentEnv() === 'prod' ? UPEnv.PROD : UPEnv.QA });
        // refreshToken() resolves ErrorResponse | Error instead of rejecting on failure.
        const tokens = await refreshTokenService.refreshToken();
        return tokens && 'applicationAccessToken' in tokens ? tokens.applicationAccessToken : null;
    } catch (error) {
        // Expected degradation (the caller surfaces the normal bind error),
        // but observability rules require the failure on record.
        appLogger.error('AuthSuite token refresh failed', {
            component: 'mvpdBindActor',
            errorMessage: error instanceof Error ? error.message : String(error),
        });
        return null;
    }
};
